import { useDefinirAtivo } from '../../../api/queries/usuarios'
import { Botao } from '../../../components/Botao'
import { data } from '../../../lib/datas'
import { TEXTOS } from '../../../lib/glossario'
import type { UsuarioAdministrado } from '../../../types/usuario'
import { EstadoDaConta, PapeisDaConta } from './EtiquetasDeConta'

const CELULA = 'px-4 py-3 align-top text-sm text-slate-700'

/**
 * As contas cadastradas.
 *
 * Tabela de verdade, com `<th scope="col">`: quem usa leitor de tela precisa ouvir "Papéis:
 * presidente do comitê" ao entrar na célula, e não uma sequência de textos soltos. É a mesma
 * decisão da listagem de chamados.
 *
 * @param props as contas e quem está logado
 * @returns a tabela
 */
export function TabelaDeContas({
  contas,
  usuarioLogadoId,
  aoRedefinirSenha,
  aoAlterarPapeis,
}: {
  contas: UsuarioAdministrado[]
  usuarioLogadoId: number | undefined
  aoRedefinirSenha: (conta: UsuarioAdministrado) => void
  aoAlterarPapeis: (conta: UsuarioAdministrado) => void
}) {
  return (
    <div className="cartao overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <caption className="sr-only">Contas com acesso ao sistema</caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {['Pessoa', 'Papéis', 'Situação', 'Cadastrada em', 'Ações'].map((titulo) => (
                <th
                  key={titulo}
                  scope="col"
                  className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-slate-600"
                >
                  {titulo}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {contas.map((conta) => (
              <LinhaDaConta
                key={conta.id}
                conta={conta}
                ehVoce={conta.id === usuarioLogadoId}
                aoRedefinirSenha={() => aoRedefinirSenha(conta)}
                aoAlterarPapeis={() => aoAlterarPapeis(conta)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LinhaDaConta({
  conta,
  ehVoce,
  aoRedefinirSenha,
  aoAlterarPapeis,
}: {
  conta: UsuarioAdministrado
  ehVoce: boolean
  aoRedefinirSenha: () => void
  aoAlterarPapeis: () => void
}) {
  const ativar = useDefinirAtivo(conta.id)

  return (
    <tr className="border-b border-slate-100 odd:bg-white even:bg-slate-50/60">
      <td className={CELULA}>
        <span className="block font-medium text-slate-900">
          {conta.nome}
          {ehVoce && <span className="ml-2 text-xs font-normal text-slate-500">(você)</span>}
        </span>
        <span className="block text-xs text-slate-500">{conta.email}</span>
      </td>
      <td className={CELULA}>
        <PapeisDaConta papeis={conta.papeis} />
      </td>
      <td className={CELULA}>
        <EstadoDaConta conta={conta} />
      </td>
      <td className={`${CELULA} whitespace-nowrap tabular-nums`}>{data(conta.criadoEm)}</td>
      <td className={CELULA}>
        <div className="flex flex-wrap gap-2">
          <Botao tamanho="pequeno" onClick={aoAlterarPapeis}>
            {TEXTOS.usuarios.alterarPapeis.botao}
          </Botao>
          {!conta.entraVinculado && (
            <Botao tamanho="pequeno" onClick={aoRedefinirSenha}>
              Redefinir senha
            </Botao>
          )}
          {/* Desativar a própria conta é recusado pelo servidor com explicação; esconder o botão
              evita o clique que só produziria a recusa. */}
          {!ehVoce && (
            <Botao
              tamanho="pequeno"
              onClick={() => ativar.mutate(!conta.ativo)}
              disabled={ativar.isPending}
            >
              {conta.ativo ? 'Desativar' : 'Reativar'}
            </Botao>
          )}
        </div>
      </td>
    </tr>
  )
}
